import React, { useRef } from "react"
import { createPortal } from "react-dom"
import { XMarkIcon, ChevronLeftIcon } from "@heroicons/react/24/outline"

const SWIPE_CLOSE_THRESHOLD_PCT = 0.35
const DEAD_ZONE = 14

export default function Drawer({ open, onClose, leftAction, rightAction, children, overlay }) {
  const touchStartX = useRef(null)
  const touchStartY = useRef(null)
  const isHorizontal = useRef(null)
  const swipeDeltaX = useRef(0)

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX
    touchStartY.current = e.touches[0].clientY
    isHorizontal.current = null
    swipeDeltaX.current = 0
  }

  const handleTouchMove = (e) => {
    if (touchStartX.current === null) return
    const deltaX = e.touches[0].clientX - touchStartX.current
    const deltaY = e.touches[0].clientY - touchStartY.current

    if (isHorizontal.current === null) {
      const absX = Math.abs(deltaX)
      const absY = Math.abs(deltaY)
      if (absX < DEAD_ZONE && absY < DEAD_ZONE) return
      isHorizontal.current = absX > absY
    }

    if (!isHorizontal.current || deltaX <= 0) return
    swipeDeltaX.current = deltaX
  }

  const handleTouchEnd = () => {
    const threshold = window.innerWidth * SWIPE_CLOSE_THRESHOLD_PCT
    if (swipeDeltaX.current >= threshold) onClose()
    touchStartX.current = null
    touchStartY.current = null
    isHorizontal.current = null
    swipeDeltaX.current = 0
  }

  // Portal to body so the drawer escapes any ancestor stacking context
  // (Account wraps content in a z-10 layer that would otherwise cap the
  // drawer below the fixed nav bar).
  if (typeof document === "undefined") return null
  return createPortal(
    <>
      {open && (
        <div
          className="fixed inset-0 z-[999999999] bg-black/30"
          onClick={onClose}
        />
      )}
      <div
        className={`fixed top-0 right-0 h-full w-full lg:w-[520px] xl:w-[560px] bg-paper z-[1000000000] flex flex-col transition-transform duration-300 ease-in-out ${open ? "translate-x-0 shadow-2xl" : "translate-x-full"}`}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="flex items-center px-5 py-4 border-b border-bordered shrink-0">
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-neutral_1 text-text_secondary"
          >
            <ChevronLeftIcon className="h-5 w-5 lg:hidden" />
            <XMarkIcon className="h-5 w-5 hidden lg:block" />
          </button>
          <div className="flex-1 flex justify-center">{leftAction}</div>
          {rightAction}
        </div>
        <div className="flex-1 overflow-y-auto overscroll-contain pb-[150px] lg:pb-0">
          {children}
        </div>
        {overlay}
      </div>
    </>,
    document.body,
  )
}
